import { useState, useEffect } from "react";
import {
  IconButton,
  Badge,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { useTranslation } from "react-i18next";
import { useAuthInfo } from "@propelauth/react";
import { Inbox } from "@novu/react";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "src/app/providers/NotificationProvider";
import { isDesktopDevice, getDeviceInfo } from "src/app/utils/notificationUtils";

function NotificationButton() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuthInfo();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [novuUnreadCount, setNovuUnreadCount] = useState(0);
  const [novuError, setNovuError] = useState<string | null>(null);

  const { unreadCount: customUnreadCount } = useNotificationContext();

  // Use Novu's unread count if available, fallback to custom implementation
  const displayUnreadCount = novuUnreadCount || customUnreadCount;

  // Debug logging for development
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      const deviceInfo = getDeviceInfo();
      console.log("🔔 NotificationButton Debug:", {
        userId: user?.userId,
        isInboxOpen,
        novuUnreadCount,
        customUnreadCount,
        displayUnreadCount,
        deviceInfo,
        isDesktopDevice: isDesktopDevice(),
        applicationIdentifier: "lJqtY_URuYl5",
        backendUrl: import.meta.env.VITE_NOVU_BACKEND_URL || "https://eu.api.novu.co",
      });
    }
  }, [
    user?.userId,
    isInboxOpen,
    novuUnreadCount,
    customUnreadCount,
    displayUnreadCount,
  ]);

  const handleNotificationClick = () => {
    setIsInboxOpen(!isInboxOpen);
  };
  
  const handleClose = () => {
    setIsInboxOpen(false);
  };

  const handleNotificationNavigation = (url: string) => {
    // Handle navigation through Novu Inbox
    // Map common notification types to app routes
    if (url.includes("room") || url.includes("clean")) {
      navigate("/apps/clean");
    } else if (url.includes("maintenance")) {
      navigate("/apps/maintenance");
    } else if (url.includes("dashboard")) {
      navigate("/apps/dashboard");
    } else {
      // For custom URLs, navigate directly
      navigate(url);
    }
    handleClose();
  };

  // Handle Novu errors
  const handleNovuError = (error: any) => {
    console.error("Novu Inbox Error:", error);
    setNovuError(error?.message || "Failed to load notifications");
  };

  // Dynamic tooltip text based on device type
  const getTooltipText = () => {
    if (isDesktopDevice()) {
      return t("NOTIFICATIONS_DESKTOP") || "Notifications (Desktop - no popups)";
    }
    return t("NOTIFICATIONS");
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Custom bell trigger that controls Novu Inbox */}
      <Tooltip title={getTooltipText()}>
        <IconButton
          color="inherit"
          onClick={handleNotificationClick}
          sx={{ mx: 0.5 }}
        >
          <Badge badgeContent={displayUnreadCount} color="error">
            <FuseSvgIcon>heroicons-outline:bell</FuseSvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Controlled Novu Inbox - no wrapper Popover */}
      {user && (
        <Inbox
          applicationIdentifier="lJqtY_URuYl5"
          subscriberId={user.userId}
          backendUrl={import.meta.env.VITE_NOVU_BACKEND_URL || "https://eu.api.novu.co"}
          socketUrl={import.meta.env.VITE_NOVU_SOCKET_URL || "https://eu.ws.novu.co"}
          {...(isInboxOpen && { open: isInboxOpen })}
          {...(handleNotificationNavigation && { routerPush: handleNotificationNavigation })}
          {...(handleNovuError && { onError: handleNovuError })}
        />
      )}
    </Box>
  );
}

export default NotificationButton;
